import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { CircleNotch } from "@phosphor-icons/react";
import QuizQuestion from "./QuizQuestion";
import { quizApi, type AnswerResultDto, type QuizQuestionDto } from "./quizApi";

/**
 * Tek bir soru bankasi sorusu, id ile: `/soru-bankasi/:questionId`.
 * `/alistirma/:practiceId` sayfasinin banka karsiligi — yalnizca egitmene acik
 * (sunucu ogrenciye 403 doner). Cevaplar kaydedilmez.
 */
export default function SingleQuizQuestionView() {
  const { questionId } = useParams();
  const id = Number(questionId);

  const [question, setQuestion] = useState<QuizQuestionDto | null>(null);
  const [result, setResult] = useState<AnswerResultDto | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // "Bastan coz" editoru ve secimi sifirlamak icin bileseni yeniden kurar
  const [round, setRound] = useState(0);

  useEffect(() => {
    setQuestion(null);
    setResult(null);
    setError(null);
    if (!Number.isInteger(id) || id <= 0) {
      setError("Geçersiz soru numarası.");
      return;
    }
    let cancelled = false;
    quizApi
      .question(id)
      .then((q) => !cancelled && setQuestion(q))
      .catch((e: any) => {
        if (cancelled) return;
        setError(e?.response?.data?.message ?? "Soru yüklenemedi.");
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleSubmit = async (
    answer: string,
    clientTestResult?: { passed: number; total: number; kind?: "structural" | "assertion" },
  ) => {
    setSubmitting(true);
    try {
      setResult(await quizApi.checkQuestion(id, { answer, clientTestResult }));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Cevap değerlendirilemedi.");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center text-white/70">
        <p>{error}</p>
      </div>
    );
  }

  if (!question) {
    return (
      <div className="flex justify-center py-20 text-white/60">
        <CircleNotch size={28} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 flex flex-col gap-6">
      <div>
        <h1 className="text-lg font-semibold text-white/90">Soru Bankası · Soru #{question.id}</h1>
        <p className="text-xs text-white/50 mt-1">
          Eğitmen önizlemesi — burada verilen cevaplar kaydedilmez, puan ve ustalığı etkilemez.
          {question.active === false && " Bu soru şu an pasif (öğrencilere gelmiyor)."}
        </p>
        {question.moduleId != null && (
          <Link
            to={`/module-practice/${question.moduleId}`}
            className="text-xs text-white/40 hover:text-white/70 underline underline-offset-2"
          >
            Bu modülün pratik setine git
          </Link>
        )}
      </div>

      <QuizQuestion
        key={`${question.id}-${round}`}
        question={question}
        index={0}
        total={1}
        result={result}
        isSubmitting={submitting}
        onSubmit={handleSubmit}
        onNext={() => {
          setResult(null);
          setRound((r) => r + 1);
        }}
        preview
      />
    </div>
  );
}
